import {assert} from 'Asserts';

import {buildDataFlowEdge} from '../../buildStaticDataFromFlowchartConfigGraphQL';
import {getFieldInfoFromDataFlowEdgeHandle} from '../../flowchartIdentifierUtils';
import {
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  StaticTemplateFlowchart,
} from '../../processFlowchartConfigEditorConstants';
import {StaticFlowchartNodeData} from '../../processFlowchartConfigEditorNodeTypes';
import {findNodePairsFulfillingTransition} from './transitionChangeUtils';
import {getFieldPairKey, FieldPairKeyParts, parseFieldPairKey} from './transitionKeyUtils';
import {findTaskSchemaNodePair} from './transitionLookupUtils';
import {Transition} from './TransitionTypes';

/**
 * Removes data flow edges from the flowchart that correspond to a planned transition's
 * field pair mappings. Used when a planned transition is removed or being replaced.
 */
export function removeDataFlowEdgesForTransition(
  flowchart: StaticTemplateFlowchart,
  transition: Transition
): Array<string> {
  const fieldPairKeys = new Set(Object.keys(transition.customMappingDataByFieldPair));
  if (fieldPairKeys.size === 0) {
    return [];
  }

  const removedEdgeIds: Array<string> = [];
  const nodes = flowchart.nodesStaticData;
  flowchart.edgesStaticData = flowchart.edgesStaticData.filter((edge) => {
    if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
      return true;
    }
    const nodePair = findTaskSchemaNodePair(edge, nodes);
    if (nodePair == null) {
      return true;
    }
    const sourceHandle = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
    const targetHandle = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);
    const key = getFieldPairKey({
      sourcePTDVId: nodePair.sourceNode.taskDefinitionVersionSpec.clientId,
      sourceSectionType: sourceHandle.sectionType,
      sourceFieldClientId: sourceHandle.fieldClientId,
      targetPTDVId: nodePair.targetNode.taskDefinitionVersionSpec.clientId,
      targetSectionType: targetHandle.sectionType,
      targetFieldClientId: targetHandle.fieldClientId,
    });
    if (fieldPairKeys.has(key)) {
      removedEdgeIds.push(edge.id);
      return false;
    }
    return true;
  });
  return removedEdgeIds;
}

/**
 * Adds data flow edges to the flowchart for each field pair mapping in a planned
 * transition. Used when a planned transition is added or being restored after edit.
 */
export function addDataFlowEdgesForTransition(
  flowchart: StaticTemplateFlowchart,
  transition: Transition
): Array<FlowchartStaticEdgeData> {
  assert(transition.isPlanned, 'Transition must be planned');

  const fieldPairKeys = Object.keys(transition.customMappingDataByFieldPair);
  if (fieldPairKeys.length === 0) {
    return [];
  }

  const fieldPairsByPTDVPair = groupFieldPairsByPTDVPair(fieldPairKeys);

  const {relevantControlFlowEdges, existingDataFlowEdgeKeys} = analyzeExistingEdges(
    flowchart.edgesStaticData,
    flowchart.nodesStaticData,
    transition
  );

  return buildEdgesForControlFlowPairs(
    relevantControlFlowEdges,
    fieldPairsByPTDVPair,
    existingDataFlowEdgeKeys,
    flowchart
  );
}

/**
 * For each relevant control flow edge, creates deduplicated data flow edges
 * for all matching field pair mappings and appends them to the flowchart.
 */
function buildEdgesForControlFlowPairs(
  relevantControlFlowEdges: Array<RelevantControlFlowEdge>,
  fieldPairsByPTDVPair: Map<string, Array<FieldPairKeyParts>>,
  existingDataFlowEdgeKeys: Set<string>,
  flowchart: StaticTemplateFlowchart
): Array<FlowchartStaticEdgeData> {
  const addedEdges: Array<FlowchartStaticEdgeData> = [];

  for (const {sourceNodeId, targetNodeId, ptdvPairKey} of relevantControlFlowEdges) {
    const matchingFieldPairs = fieldPairsByPTDVPair.get(ptdvPairKey);
    if (matchingFieldPairs == null) {
      continue;
    }

    for (const parsed of matchingFieldPairs) {
      const edge = buildDataFlowEdge({
        sourceNodeId,
        targetNodeId,
        fromFieldId: parsed.sourceFieldClientId,
        fromFieldSectionType: parsed.sourceSectionType,
        toFieldId: parsed.targetFieldClientId,
        toFieldSectionType: parsed.targetSectionType,
      });

      const edgeKey = `${edge.sourceHandle}::${edge.targetHandle}`;
      if (existingDataFlowEdgeKeys.has(edgeKey)) {
        continue;
      }

      existingDataFlowEdgeKeys.add(edgeKey);
      flowchart.edgesStaticData.push(edge);
      addedEdges.push(edge);
    }
  }

  return addedEdges;
}

export interface RelevantControlFlowEdge {
  sourceNodeId: string;
  targetNodeId: string;
  ptdvPairKey: string;
}

/**
 * Collects control flow edges whose endpoints match the transition's source/target PTDVs,
 * and builds a deduplicated set of existing data flow edge keys.
 */
function analyzeExistingEdges(
  edges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>,
  transition: Transition
): {relevantControlFlowEdges: Array<RelevantControlFlowEdge>; existingDataFlowEdgeKeys: Set<string>} {
  const existingDataFlowEdgeKeys = new Set<string>();
  for (const edge of edges) {
    if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
      existingDataFlowEdgeKeys.add(`${edge.sourceHandle}::${edge.targetHandle}`);
    }
  }

  return {
    relevantControlFlowEdges: findNodePairsFulfillingTransition({nodes, edges, transition}),
    existingDataFlowEdgeKeys,
  };
}

/**
 * Parses field pair key strings and groups the results by (sourcePTDVId, targetPTDVId)
 * so that each control flow edge can look up its matching field pairs in O(1).
 */
function groupFieldPairsByPTDVPair(fieldPairKeys: Array<string>): Map<string, Array<FieldPairKeyParts>> {
  const map = new Map<string, Array<FieldPairKeyParts>>();
  for (const key of fieldPairKeys) {
    const parsed = parseFieldPairKey(key);
    if (parsed == null) {
      continue;
    }
    const ptdvPairKey = `${parsed.sourcePTDVId}::${parsed.targetPTDVId}`;
    let group = map.get(ptdvPairKey);
    if (group == null) {
      group = [];
      map.set(ptdvPairKey, group);
    }
    group.push(parsed);
  }
  return map;
}
